import { z } from '@repo/schema'
import { getDataWrapperSchema } from '@repo/schema/data-wrapper'
import {
  ConnectXeroCreditNoteSchema,
  ConnectXeroInvoiceSchema,
  ConnectXeroQuoteSchema,
  CreateXeroInvoiceSchema,
  CreateXeroQuoteSchema,
  GetXeroCreditNotePreviewSchema,
  GetXeroCreditNoteSchema,
  GetXeroInvoiceFormDefaultsSchema,
  GetXeroInvoicePreviewSchema,
  GetXeroInvoiceSchema,
  GetXeroQuoteFormDefaultsSchema,
  GetXeroQuotePreviewSchema,
  GetXeroQuoteSchema,
  UpdateXeroInvoiceSchema,
  UpdateXeroQuoteSchema,
} from '@repo/schema/invoice'
import { GetUserSchema } from '@repo/schema/user'
import { UpFetch } from '@repo/upfetch'

import { createProcedure } from '../../lib/procedure'

// The lists include who added each document, for the row's "Added by" details.
const GetXeroInvoiceWithUserSchema = GetXeroInvoiceSchema.extend({
  user: GetUserSchema.nullish(),
})

const GetXeroCreditNoteWithUserSchema = GetXeroCreditNoteSchema.extend({
  user: GetUserSchema.nullish(),
})

const GetXeroQuoteWithUserSchema = GetXeroQuoteSchema.extend({
  user: GetUserSchema.nullish(),
})

export const createXeroRoute = (upFetch: UpFetch) => {
  const p = createProcedure(upFetch)

  return {
    // POST /loads/{loadId}/xero-credit-notes/connect
    connectLoadXeroCreditNote: p
      .path(z.object({ loadId: z.number() }), p => [
        'loads',
        p.loadId,
        'xero-credit-notes',
        'connect',
      ])
      .input(ConnectXeroCreditNoteSchema)
      .mutation(({ input, path, upFetch }) =>
        upFetch(path, {
          body: input,
          method: 'POST',
          schema: getDataWrapperSchema(GetXeroCreditNoteWithUserSchema),
        }),
      ),

    connectLoadXeroInvoice: p
      .path(z.object({ loadId: z.number() }), p => ['loads', p.loadId, 'xero-invoices', 'connect'])
      .input(ConnectXeroInvoiceSchema)
      .mutation(({ input, path, upFetch }) =>
        upFetch(path, {
          body: input,
          method: 'POST',
          schema: getDataWrapperSchema(GetXeroInvoiceSchema),
        }),
      ),

    // POST /loads/{loadId}/xero-quotes/connect
    connectLoadXeroQuote: p
      .path(z.object({ loadId: z.number() }), p => ['loads', p.loadId, 'xero-quotes', 'connect'])
      .input(ConnectXeroQuoteSchema)
      .mutation(({ input, path, upFetch }) =>
        upFetch(path, {
          body: input,
          method: 'POST',
          schema: getDataWrapperSchema(GetXeroQuoteSchema),
        }),
      ),

    // POST /loads/{loadId}/xero-invoices
    createLoadXeroInvoice: p
      .path(z.object({ loadId: z.number() }), p => ['loads', p.loadId, 'xero-invoices'])
      .input(CreateXeroInvoiceSchema)
      .mutation(({ input, path, upFetch }) =>
        upFetch(path, {
          body: input,
          method: 'POST',
          schema: getDataWrapperSchema(GetXeroInvoiceSchema),
        }),
      ),

    // POST /loads/{loadId}/xero-quotes
    createLoadXeroQuote: p
      .path(z.object({ loadId: z.number() }), p => ['loads', p.loadId, 'xero-quotes'])
      .input(CreateXeroQuoteSchema)
      .mutation(({ input, path, upFetch }) =>
        upFetch(path, {
          body: input,
          method: 'POST',
          schema: getDataWrapperSchema(GetXeroQuoteSchema),
        }),
      ),

    // GET /loads/{loadId}/xero-invoices/{id}/download
    downloadLoadXeroInvoice: p
      .path(z.object({ id: z.number(), loadId: z.number() }), p => [
        'loads',
        p.loadId,
        'xero-invoices',
        p.id,
        'download',
      ])
      .query(({ path, upFetch }) => upFetch(path, { parseResponse: response => response.blob() })),

    // GET /loads/{loadId}/xero-quotes/{id}/download
    downloadLoadXeroQuote: p
      .path(z.object({ id: z.number(), loadId: z.number() }), p => [
        'loads',
        p.loadId,
        'xero-quotes',
        p.id,
        'download',
      ])
      .query(({ path, upFetch }) => upFetch(path, { parseResponse: response => response.blob() })),

    // POST /xero/companies/:id/export
    exportCompanyToXero: p
      .path(z.object({ id: z.number() }), p => ['xero', 'companies', p.id, 'export'])
      .mutation(({ path, upFetch }) => upFetch(path, { method: 'POST' })),

    // POST /loads/{loadId}/xero-credit-notes/preview
    getLoadXeroCreditNotePreview: p
      .path(z.object({ loadId: z.number() }), p => [
        'loads',
        p.loadId,
        'xero-credit-notes',
        'preview',
      ])
      .input(z.object({ url: z.string() }))
      .mutation(({ input, path, upFetch }) =>
        upFetch(path, {
          body: input,
          method: 'POST',
          schema: getDataWrapperSchema(GetXeroCreditNotePreviewSchema),
        }),
      ),

    // GET /loads/{loadId}/xero-credit-notes
    getLoadXeroCreditNotes: p
      .path(z.object({ loadId: z.number() }), p => ['loads', p.loadId, 'xero-credit-notes'])
      .query(({ path, upFetch }) =>
        upFetch(path, {
          params: { i: 'user' },
          schema: getDataWrapperSchema(GetXeroCreditNoteWithUserSchema.array()),
        }),
      ),

    // GET /loads/{loadId}/xero-invoices/{id}
    getLoadXeroInvoice: p
      .path(z.object({ id: z.number(), loadId: z.number() }), p => [
        'loads',
        p.loadId,
        'xero-invoices',
        p.id,
      ])
      .query(({ path, upFetch }) =>
        upFetch(path, { schema: getDataWrapperSchema(GetXeroInvoiceSchema) }),
      ),

    // GET /loads/{loadId}/xero-invoices/form-defaults
    getLoadXeroInvoiceFormDefaults: p
      .path(z.object({ loadId: z.number() }), p => [
        'loads',
        p.loadId,
        'xero-invoices',
        'form-defaults',
      ])
      .query(({ path, upFetch }) =>
        upFetch(path, { schema: getDataWrapperSchema(GetXeroInvoiceFormDefaultsSchema) }),
      ),

    getLoadXeroInvoicePreview: p
      .path(z.object({ loadId: z.number() }), p => ['loads', p.loadId, 'xero-invoices', 'preview'])
      .input(z.object({ url: z.string() }))
      .mutation(({ input, path, upFetch }) =>
        upFetch(path, {
          body: input,
          method: 'POST',
          schema: getDataWrapperSchema(GetXeroInvoicePreviewSchema),
        }),
      ),

    // GET /loads/{loadId}/xero-invoices
    getLoadXeroInvoices: p
      .path(z.object({ loadId: z.number() }), p => ['loads', p.loadId, 'xero-invoices'])
      .query(({ path, upFetch }) =>
        upFetch(path, {
          params: { i: 'user' },
          schema: getDataWrapperSchema(GetXeroInvoiceWithUserSchema.array()),
        }),
      ),

    // GET /loads/{loadId}/xero-quotes/{id}
    getLoadXeroQuote: p
      .path(z.object({ id: z.number(), loadId: z.number() }), p => [
        'loads',
        p.loadId,
        'xero-quotes',
        p.id,
      ])
      .query(({ path, upFetch }) =>
        upFetch(path, { schema: getDataWrapperSchema(GetXeroQuoteSchema) }),
      ),

    // GET /loads/{loadId}/xero-quotes/form-defaults
    getLoadXeroQuoteFormDefaults: p
      .path(z.object({ loadId: z.number() }), p => [
        'loads',
        p.loadId,
        'xero-quotes',
        'form-defaults',
      ])
      .query(({ path, upFetch }) =>
        upFetch(path, { schema: getDataWrapperSchema(GetXeroQuoteFormDefaultsSchema) }),
      ),

    // POST /loads/{loadId}/xero-quotes/preview
    getLoadXeroQuotePreview: p
      .path(z.object({ loadId: z.number() }), p => ['loads', p.loadId, 'xero-quotes', 'preview'])
      .input(z.object({ url: z.string() }))
      .mutation(({ input, path, upFetch }) =>
        upFetch(path, {
          body: input,
          method: 'POST',
          schema: getDataWrapperSchema(GetXeroQuotePreviewSchema),
        }),
      ),

    // GET /loads/{loadId}/xero-quotes
    getLoadXeroQuotes: p
      .path(z.object({ loadId: z.number() }), p => ['loads', p.loadId, 'xero-quotes'])
      .query(({ path, upFetch }) =>
        upFetch(path, {
          params: { i: 'user' },
          schema: getDataWrapperSchema(GetXeroQuoteWithUserSchema.array()),
        }),
      ),
    // DELETE /loads/{loadId}/xero-credit-notes/{id}
    removeLoadXeroCreditNote: p
      .path(z.object({ id: z.number(), loadId: z.number() }), p => [
        'loads',
        p.loadId,
        'xero-credit-notes',
        p.id,
      ])
      .mutation(({ path, upFetch }) => upFetch(path, { method: 'DELETE' })),

    // DELETE /loads/{loadId}/xero-invoices/{id}
    removeLoadXeroInvoice: p
      .path(z.object({ id: z.number(), loadId: z.number() }), p => [
        'loads',
        p.loadId,
        'xero-invoices',
        p.id,
      ])
      .mutation(({ path, upFetch }) => upFetch(path, { method: 'DELETE' })),

    // DELETE /loads/{loadId}/xero-quotes/{id}
    removeLoadXeroQuote: p
      .path(z.object({ id: z.number(), loadId: z.number() }), p => [
        'loads',
        p.loadId,
        'xero-quotes',
        p.id,
      ])
      .mutation(({ path, upFetch }) => upFetch(path, { method: 'DELETE' })),

    // DELETE /xero/companies/:id/unlink
    unlinkCompanyFromXero: p
      .path(z.object({ id: z.number() }), p => ['xero', 'companies', p.id, 'unlink'])
      .mutation(({ path, upFetch }) => upFetch(path, { method: 'DELETE' })),

    // PUT /loads/{loadId}/xero-invoices/{id}
    updateLoadXeroInvoice: p
      .path(z.object({ id: z.number(), loadId: z.number() }), p => [
        'loads',
        p.loadId,
        'xero-invoices',
        p.id,
      ])
      .input(UpdateXeroInvoiceSchema)
      .mutation(({ input, path, upFetch }) =>
        upFetch(path, {
          body: input,
          method: 'PUT',
          schema: getDataWrapperSchema(GetXeroInvoiceSchema),
        }),
      ),

    // PUT /loads/{loadId}/xero-quotes/{id}
    updateLoadXeroQuote: p
      .path(z.object({ id: z.number(), loadId: z.number() }), p => [
        'loads',
        p.loadId,
        'xero-quotes',
        p.id,
      ])
      .input(UpdateXeroQuoteSchema)
      .mutation(({ input, path, upFetch }) =>
        upFetch(path, {
          body: input,
          method: 'PUT',
          schema: getDataWrapperSchema(GetXeroQuoteSchema),
        }),
      ),
  }
}
